import React, { useState, useEffect } from 'react';
import { QrCode, Mail, Phone, User } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useLinkedModalSync } from '@/hooks/useLinkedModalSync';

interface Promo {
  id: string;
  nome: string;
  objetivo: string;
  title: string;
  url: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  duration: number;
  interval: number;
  Typepromo: string;
  screen_ids: string[];
  active: boolean;
  user_id: string;
  campaign_id?: string;
  Solicitar?: string | string[];
  'Modal visível'?: 'HIDDEN' | 'TEMPORARY' | 'PERMANENT' | 'LINKED';
  selected_media_ids?: string[] | string; // Para modo LINKED
}

interface PromoDisplayModalProps {
  promo: Promo;
  onClose: () => void;
  deviceCode?: string;
  screenId?: string;
  // Dados para modo LINKED - sincronização com mídia
  currentMediaIndex?: number;
  filteredMedias?: Array<{
    id: string;
    display_time?: number;
    title?: string;
    [key: string]: unknown;
  }>;
  bottomOffset?: number;
}

const generateQRCodeUrl = (url: string) => {
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
};

const PromoDisplayModal: React.FC<PromoDisplayModalProps> = ({
  promo,
  onClose,
  deviceCode,
  screenId,
  currentMediaIndex = 0,
  filteredMedias = [],
  bottomOffset = 16
}) => {
  // Verificar se está no modo LINKED
  const isLinkedMode = promo['Modal visível'] === 'LINKED';

  // Parse de selected_media_ids com cache
  const selectedMediaIds = React.useMemo(() => {
    const raw = promo.selected_media_ids as unknown;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter((v) => typeof v === 'string');
    if (typeof raw === 'string') {
      const s = raw.trim();
      let parsed: string[] | null = null;
      if (s.startsWith('[') && s.endsWith(']')) {
        try { parsed = JSON.parse(s); } catch (e) { parsed = null; }
      }
      if (!parsed && s.startsWith('{') && s.endsWith('}')) {
        const inner = s.slice(1, -1);
        parsed = inner.split(',').map(x => x.trim().replace(/^"|"$/g, '').replace(/^'|'$/g, ''));
      }
      if (!parsed) {
        parsed = s.split(',').map(x => x.trim()).filter(Boolean);
      }
      return (parsed || []).filter(v => typeof v === 'string');
    }
    return [];
  }, [promo.selected_media_ids]);

  // Hook para sincronizar com mídia atual (modo LINKED)
  const {
    modalDuration: linkedDuration,
    secondsLeft: linkedSecondsLeft,
    progress: linkedProgress,
    isActive: linkedIsActive,
    currentMediaTitle
  } = useLinkedModalSync({
    currentMediaIndex,
    filteredMedias,
    isLinkedMode,
    selectedMediaIds,
    onModalTimeExpired: () => {
      console.log('🔗 [PROMO MODAL] Modal LINKED expirou com a mídia - fechando');
      onClose();
    },
    onMediaNotSelected: () => {
      console.log('🔗 [PROMO MODAL] Mídia atual não está selecionada - fechando modal LINKED');
      onClose();
    }
  });

  // Estado para contador regressivo - diferente para cada modo
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (isLinkedMode) {
      return linkedSecondsLeft; // Sincronizado com mídia
    } else if (promo['Modal visível'] === 'TEMPORARY') {
      return promo.duration; // Tempo configurado da promoção
    }
    return 0; // PERMANENT ou HIDDEN não tem contador
  });

  // Estado para controlar se o modal deve ser exibido
  const [shouldShow, setShouldShow] = useState(promo['Modal visível'] !== 'HIDDEN');

  // Atualizar shouldShow quando o status do modal mudar
  useEffect(() => {
    setShouldShow(promo['Modal visível'] !== 'HIDDEN');
  }, [promo['Modal visível']]);

  // Sincronizar contador com modo LINKED
  useEffect(() => {
    if (isLinkedMode) {
      setSecondsLeft(linkedSecondsLeft);
      const media = filteredMedias[currentMediaIndex];
      const mediaTime = media?.display_time || 'N/A';
      console.log(`🟦 [DEPURACAO LINKED] Mídia atual:`, media);
      console.log(`🟦 [DEPURACAO LINKED] Tempo da mídia (display_time):`, mediaTime);
      console.log(`🟦 [DEPURACAO LINKED] Tempo do modal (linkedSecondsLeft):`, linkedSecondsLeft);
      console.log(`🟦 [DEPURACAO LINKED] Título da mídia:`, currentMediaTitle);
    }
  }, [isLinkedMode, linkedSecondsLeft, currentMediaTitle]);

  // Listener para mudanças no status do modal
  useEffect(() => {
    const handleModalStatusChange = (event: CustomEvent) => {
      if (event.detail?.promoId === promo.id && event.detail?.action === 'modal_status_changed') {
        console.log(`🔄 [PROMO_DISPLAY] Status do modal mudou para: ${event.detail.newStatus}`);
        console.log(`🔄 [PROMO_DISPLAY] Promoção: ${promo.nome}`);

        if (event.detail.newStatus === 'HIDDEN') {
          console.log('🔄 [PROMO_DISPLAY] Modal deve ser oculto, fechando...');
          onClose();
        } else if (event.detail.newStatus === 'TEMPORARY') {
          console.log('🔄 [PROMO_DISPLAY] Modal agora é TEMPORARY, reiniciando timer...');
          setSecondsLeft(promo.duration);
          setShouldShow(true);
        } else if (event.detail.newStatus === 'PERMANENT') {
          console.log('🔄 [PROMO_DISPLAY] Modal agora é PERMANENT, removendo timer...');
          setSecondsLeft(0);
          setShouldShow(true);
        } else if (event.detail.newStatus === 'LINKED') {
          console.log('🔗 [PROMO_DISPLAY] Modal agora é LINKED, sincronizando com mídia...');
          setShouldShow(true);
          // O linkedSync hook será ativado automaticamente
        }
      }
    };

    window.addEventListener('promotionUpdated', handleModalStatusChange as EventListener);

    return () => {
      window.removeEventListener('promotionUpdated', handleModalStatusChange as EventListener);
    };
  }, [promo.id, promo.nome, promo.duration, onClose]);

  const isHidden = promo['Modal visível'] === 'HIDDEN' || !shouldShow;

  const [leadData, setLeadData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponExpiresAt, setCouponExpiresAt] = useState<number | null>(null);
  const [couponRemaining, setCouponRemaining] = useState<number>(60);

  // Timer regressivo (apenas para modo TEMPORARY - LINKED e PERMANENT não usam)
  useEffect(() => {
    if (isLinkedMode) {
      console.log('🔗 [PROMO MODAL] Modo LINKED - Timer gerenciado pelo hook de sincronização');
      return;
    }

    if (promo['Modal visível'] !== 'TEMPORARY') {
      console.log('📏 [PROMO MODAL] Modal não é TEMPORARY - contador desabilitado');
      setSecondsLeft(0);
      return;
    }

    setSecondsLeft(promo.duration);
    let interval: NodeJS.Timeout | undefined;
    if (promo.duration > 0) {
      interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [promo.duration, promo['Modal visível'], isLinkedMode]);

  // Auto-close modal after duration (apenas para modo TEMPORARY - LINKED é gerenciado pelo hook)
  useEffect(() => {
    if (isLinkedMode) {
      console.log('🔗 [PROMO MODAL] Modo LINKED - Auto-close gerenciado pelo hook de sincronização');
      return;
    }

    if (promo['Modal visível'] !== 'TEMPORARY') {
      console.log('📏 [PROMO MODAL] Modal não é TEMPORARY - auto-close desabilitado');
      return;
    }

    if (secondsLeft === 0 && promo.duration > 0) {
      console.log('⏰ [PROMO MODAL] Tempo esgotado, fechando modal');
      setTimeout(() => {
        onClose();
      }, 500);
    }
  }, [secondsLeft, promo.duration, promo['Modal visível'], onClose, isLinkedMode]);

  const generateCouponRaw = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 7; i++) {
      s += chars[Math.floor(Math.random() * chars.length)];
    }
    return s;
  };

  const formatCoupon = (raw: string) => {
    const r = raw.replace(/[^A-Z0-9]/g, '').toUpperCase().slice(0, 7);
    return `${r.slice(0, 4)}-${r.slice(4, 7)}`;
  };

  const generateUniqueCoupon = async (): Promise<string> => {
    for (let attempt = 0; attempt < 5; attempt++) {
      const code = formatCoupon(generateCouponRaw());
      const { count, error } = await supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('CUPOM', code);
      if (!error && (!count || count === 0)) return code;
    }
    return formatCoupon(String(Date.now()).slice(-7));
  };

  useEffect(() => {
    if (!showSuccess || !couponExpiresAt) return;
    const tick = () => {
      const remain = Math.max(0, Math.ceil((Number(couponExpiresAt) - Date.now()) / 1000));
      setCouponRemaining(remain);
      if (remain <= 0) onClose();
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [showSuccess, couponExpiresAt, onClose]);
  const formatPhoneInput = (value: string): string => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 11);
    const ddd = digits.slice(0, 2);
    const rest = digits.slice(2);
    if (digits.length <= 2) return ddd;
    if (digits.length <= 6) return `(${ddd}) ${rest}`;
    if (digits.length <= 10) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
    return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
  };

  // Função para lidar com envio do lead
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const solicitar = Array.isArray(promo.Solicitar) ? promo.Solicitar : [promo.Solicitar];
    const requireEmail = solicitar?.includes('e-mail') || solicitar?.includes('E-mail') || solicitar?.includes('ambos');
    const requirePhone = solicitar?.includes('whatsapp') || solicitar?.includes('WhatsApp') || solicitar?.includes('ambos');
    const emailOk = !requireEmail || (!!leadData.email && /.+@.+\..+/.test(leadData.email));
    const phoneDigits = (leadData.phone || '').replace(/\D/g, '');
    const phoneOk = !requirePhone || (!!leadData.phone && phoneDigits.length >= 10);
    const nameOk = !!leadData.name && leadData.name.trim().length > 0;

    if (!emailOk || !phoneOk) {
      alert('Verifique os campos: e-mail e WhatsApp precisam estar válidos.');
      return;
    }
    if (!nameOk) {
      alert('Informe seu nome.');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('📤 [PROMO MODAL] Tentando salvar lead:', { leadData, promoId: promo.id, deviceCode, screenId });
      const createdAt = new Date().toISOString();
      const promocaoLead = promo.nome || promo.title || '';
      const code = await generateUniqueCoupon();

      // Estrutura real da tabela leads: name (nullable), email (NOT NULL), telefone (NOT NULL), promocaoLead (NOT NULL), created_at (DEFAULT NOW())
      const payload1 = {
        name: leadData.name || null,
        email: leadData.email || null,
        telefone: phoneDigits || null,
        promocaoLead: promocaoLead || null,
        CUPOM: code,
        user_id: promo.user_id // Adicionando user_id para vincular ao dono da promoção
      };
      let { data, error } = await supabase
        .from('leads')
        .insert([payload1])
        .select('*')
        .single();

      // Fallback para esquemas alternativos (usa 'phone' em vez de 'telefone')
      if (error) {
        console.warn('⚠️ [PROMO MODAL] Tentativa 1 falhou:', error);
        const payload2 = {
          name: leadData.name || null,
          email: leadData.email || null,
          phone: phoneDigits || null,
          promocaoLead: promocaoLead || null,
          CUPOM: code
        };
        const fallback = await supabase
          .from('leads')
          .insert([payload2])
          .select('*')
          .single();
        data = fallback.data;
        error = fallback.error;
      }

      // Fallback 3: sem user_id (possível política RLS) e aceitando telefone
      if (error) {
        console.warn('⚠️ [PROMO MODAL] Tentativa 2 falhou, tentando sem user_id:', error);
        const payload3 = payload1;
        const fallback3 = await supabase
          .from('leads')
          .insert([payload3])
          .select('*')
          .single();
        data = fallback3.data;
        error = fallback3.error;
      }

      if (error) {
        console.error('❌ [PROMO MODAL] Erro ao salvar lead:', error);
        alert('Erro ao enviar dados. Tente novamente.');
        diagnosticarTabela();
        setIsSubmitting(false);
        return;
      }

      console.log('✅ [PROMO MODAL] Lead salvo com sucesso:', { data, createdAt, promocaoLead });
      setCouponCode(code);
      setCouponExpiresAt(Date.now() + 60000);
      setShowSuccess(true);

    } catch (error) {
      console.error('❌ [PROMO MODAL] Erro inesperado:', error);
      alert('Erro ao processar dados. Tente novamente.');
      setIsSubmitting(false);
    }
  };

  // SVG Circular Progress - diferentes cálculos para cada modo
  const radius = 20;
  const stroke = 4;
  const size = radius * 2 + stroke * 2;
  const normalizedRadius = radius;
  const center = radius + stroke;
  const circumference = normalizedRadius * 2 * Math.PI;

  // Calcular progresso baseado no modo
  let progress = 0;
  let totalDuration = 0;

  if (isLinkedMode) {
    // Modo LINKED: usar progresso do hook de sincronização
    progress = linkedProgress;
    totalDuration = linkedDuration;
  } else if (promo['Modal visível'] === 'TEMPORARY') {
    // Modo TEMPORARY: usar duração configurada da promoção
    totalDuration = promo.duration;
    progress = totalDuration > 0 ? (totalDuration - secondsLeft) / totalDuration : 0;
  }

  // Log de progresso LINKED movido para useEffect para evitar setState durante render
  useEffect(() => {
    if (isLinkedMode) {
      console.log(`🔗 [PROMO MODAL] Progresso LINKED: ${Math.round(progress * 100)}% (${linkedSecondsLeft}s de ${totalDuration}s)`);
    }
  }, [isLinkedMode, progress, linkedSecondsLeft, totalDuration]);

  const strokeDashoffset = circumference * (1 - progress);

  // Cor dinâmica do círculo baseada no progresso real
  let circleColor = '#22c55e'; // verde
  if (progress >= 0.5 && progress < 0.8) {
    circleColor = '#facc15'; // amarelo
  } else if (progress >= 0.8) {
    circleColor = '#ef4444'; // vermelho
  }

  // Cor especial para modo LINKED
  if (isLinkedMode) {
    circleColor = '#3b82f6'; // azul para indicar sincronização com mídia
  }

  const couponTotalDuration = 60;
  const couponProgress = couponTotalDuration > 0 ? (couponTotalDuration - couponRemaining) / couponTotalDuration : 0;
  let couponCircleColor = '#22c55e';
  if (couponProgress > 0.5 && couponProgress <= 0.7) {
    couponCircleColor = '#facc15';
  } else if (couponProgress > 0.7) {
    couponCircleColor = '#ef4444';
  }
  const couponStrokeDashoffset = circumference * (1 - couponProgress);

  if (showSuccess) {
    return (
      <div className="fixed left-0 bottom-0 bg-black/80 flex items-end justify-start z-[9999] w-full h-full">
        <div className="bg-white rounded-lg w-[380px] mx-4 mb-4 relative flex flex-col justify-center items-center p-8">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-2">CUPOM PROMOCIONAL</h2>
            <p className="text-gray-600">Anote e salve seu código</p>
            <div className="mt-4 text-3xl font-extrabold font-mono tracking-widest uppercase text-green-700 bg-green-50 border-2 border-green-600 rounded-md px-4 py-2 shadow-sm">
              {couponCode}
            </div>
            <p className="text-sm text-gray-500 mt-2">Fechará em</p>
            <div className="mt-3 flex justify-center">
              <svg height={size} width={size}>
                <circle
                  stroke="#e5e7eb"
                  fill="transparent"
                  strokeWidth={stroke}
                  r={normalizedRadius}
                  cx={center}
                  cy={center}
                />
                <circle
                  stroke={couponCircleColor}
                  fill="transparent"
                  strokeWidth={stroke}
                  strokeDasharray={circumference}
                  strokeDashoffset={couponStrokeDashoffset}
                  r={normalizedRadius}
                  cx={center}
                  cy={center}
                  style={{ transition: 'stroke-dashoffset 0.3s linear, stroke 0.3s', transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                />
                <text
                  x={center}
                  y={center + 5}
                  textAnchor="middle"
                  fontSize="14"
                  fill="#1f2937"
                  fontWeight="bold"
                >
                  {couponRemaining}
                </text>
              </svg>
            </div>
            <div className="mt-1 text-center text-xs text-gray-500">segundos</div>
          </div>
        </div>
      </div>
    );
  }

  if (isHidden) {
    return null;
  }

  return (
    <div className="fixed left-0 bg-transparent flex items-end justify-start z-[9999] w-full h-full" style={{ bottom: bottomOffset }}>
      <div className="bg-white rounded-lg w-[380px] max-h-[90vh] mx-4 mb-0 relative flex flex-col">


        <div className="p-6 w-full flex-grow flex flex-col justify-center items-center">
          <h1 className="text-2xl font-bold text-center mb-2 text-gray-800">
            {promo.title}
          </h1>
          {promo.objetivo && (
            <p className="text-center text-gray-600 mb-4 text-sm">
              {promo.objetivo}
            </p>
          )}

          {promo.Typepromo === 'QR code' && promo.url && (
            <div className="text-center mb-4">
              <div className="inline-block p-3 bg-gray-100 rounded-lg">
                <img
                  src={generateQRCodeUrl(promo.url)}
                  alt="QR Code"
                  className="w-40 h-40 mx-auto"
                />
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Escaneie o QR Code para acessar a promoção
              </p>
              {/* URL removida para todos os status */}
            </div>
          )}

          {promo.Typepromo === 'Captação de Lead' && (
            <form onSubmit={handleLeadSubmit} className="space-y-3 w-full px-4">
              {/* Input de nome (sempre visível) */}
              <div>
                <label htmlFor="lead-name" className="sr-only">Nome</label>
                <input
                  id="lead-name"
                  name="nome"
                  type="text"
                  autoComplete="name"
                  value={leadData.name}
                  onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Digite seu nome"
                />
              </div>
              {/* Input de e-mail */}
              {(promo.Solicitar === 'e-mail' || promo.Solicitar === 'ambos' || promo.Solicitar === 'E-mail') && (
                <div>
                  <label htmlFor="lead-email" className="sr-only">E-mail</label>
                  <input
                    id="lead-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Digite seu e-mail"
                  />
                </div>
              )}

              {/* Input de WhatsApp/celular */}
              {(promo.Solicitar === 'whatsapp' || promo.Solicitar === 'ambos' || promo.Solicitar === 'WhatsApp') && (
                <div>
                  <label htmlFor="lead-phone" className="sr-only">WhatsApp</label>
                  <input
                    id="lead-phone"
                    name="whatsapp"
                    type="tel"
                    autoComplete="tel"
                    inputMode="numeric"
                    maxLength={16}
                    required
                    value={leadData.phone}
                    onChange={(e) => setLeadData({ ...leadData, phone: formatPhoneInput(e.target.value) })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="(11) 90000-0000"
                  />
                </div>
              )}

              {/* Botão "Pegar promoção" */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
              >
                {isSubmitting ? 'Enviando...' : 'Pegar promoção'}
              </button>
            </form>
          )}

        </div>

        {/* Contador regressivo circular - APENAS para TEMPORARY */}
        {promo['Modal visível'] === 'TEMPORARY' && (
          <div className="w-full flex justify-between items-center p-4 border-t border-gray-200">
            {/* Contador circular */}
            <div className="w-full flex justify-end">
              <svg height={size} width={size}>
                <circle
                  stroke="#e5e7eb"
                  fill="transparent"
                  strokeWidth={stroke}
                  r={normalizedRadius}
                  cx={center}
                  cy={center}
                />
                <circle
                  stroke={circleColor}
                  fill="transparent"
                  strokeWidth={stroke}
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  r={normalizedRadius}
                  cx={center}
                  cy={center}
                  style={{ transition: 'stroke-dashoffset 0.3s linear, stroke 0.3s', transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                />
                <text
                  x={center}
                  y={center + 5}
                  textAnchor="middle"
                  fontSize="14"
                  fill="#1f2937"
                  fontWeight="bold"
                >
                  {secondsLeft}
                </text>
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoDisplayModal;
const diagnosticarTabela = async () => {
  console.log('🔍 DIAGNÓSTICO: Verificando tabela leads...');
  try {
    const { data: checkData, error: checkError } = await supabase.from('leads').select('*').limit(1);
    if (checkError) {
      console.error('❌ ERRO no SELECT:', checkError);
    } else {
      console.log('✅ Tabela acessível', checkData?.[0] || 'Vazia');
    }
    const { data: insertData, error: insertError } = await supabase
      .from('leads')
      .insert([{ name: 'Teste Diagnóstico', email: `teste${Date.now()}@diagnostico.com`, telefone: '11999999999', promocaoLead: 'TESTE' }])
      .select('*')
      .single();
    if (insertError) {
      console.error('❌ ERRO no INSERT:', insertError);
    } else if (insertData?.id) {
      console.log('✅ INSERT funcionando!', insertData);
      await supabase.from('leads').delete().eq('id', insertData.id);
    }
  } catch (err) {
    console.error('❌ ERRO INESPERADO:', err);
  }
};
